import { useState } from 'react';
import { Avatar, Box, Chip, Collapse, Stack, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const isImageAttachment = (att) =>
  (att?.content_type || '').startsWith('image/') ||
  /\.(png|jpe?g|gif|webp|bmp)$/i.test(att?.filename || '');

// Swap `cid:<content-id>` references in an HTML body for the stored
// attachment URLs so embedded images actually render. Returns the rewritten
// html plus the ids of attachments consumed inline (they're already visible
// in the body, so the thumbnail strip skips them).
function resolveInlineImages(html, attachments) {
  if (!html || !attachments?.length) return { html, inlineIds: new Set() };
  const inlineIds = new Set();
  let out = html;
  attachments.forEach((att) => {
    if (!att.content_id || !att.file_url) return;
    const needle = `cid:${att.content_id}`;
    if (out.includes(needle)) {
      out = out.split(needle).join(att.file_url);
      inlineIds.add(att.id);
    }
  });
  return { html: out, inlineIds };
}

// Collapsible single-message renderer for an email conversation, shared by the
// Client Inbox page and the tasks-page email panel. Moved verbatim from
// ClientInboxPage.jsx.
function ThreadMessage({ msg, defaultExpanded, isInternal }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const bodyIsHtml = msg.body_text && (msg.body_text.includes('<p>') || msg.body_text.includes('<br') || msg.body_text.includes('<div'));
  const { html: resolvedHtml, inlineIds } = resolveInlineImages(
    bodyIsHtml ? msg.body_text : '',
    msg.attachments,
  );
  const imageAttachments = (msg.attachments || []).filter(
    (att) => isImageAttachment(att) && att.file_url && !inlineIds.has(att.id),
  );
  const fileAttachments = (msg.attachments || []).filter(
    (att) => !isImageAttachment(att) && !inlineIds.has(att.id),
  );

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: isInternal ? 'info.main' : 'grey.300',
        borderRadius: 1.5,
        overflow: 'hidden',
        mb: 1,
        ml: 0,
      }}
    >
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          px: 2,
          py: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flex={1} minWidth={0}>
          <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
            {(msg.from_email || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Stack minWidth={0} flex={1}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {msg.from_email}
              {isInternal ? (
                <Chip label="Us" size="small" color="info" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }} />
              ) : null}
            </Typography>
            {!expanded ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {(msg.body_text || '').replace(/<[^>]*>/g, '').slice(0, 120)}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" whiteSpace="nowrap" ml={1}>
          {msg.received_at ? dayjs(msg.received_at).fromNow() : ''}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box px={2} pb={2}>
          <Typography variant="caption" color="text.secondary">
            To: {(msg.to_addresses || []).join(', ') || '—'}
            {msg.cc_addresses?.length ? ` · CC: ${msg.cc_addresses.join(', ')}` : ''}
          </Typography>
          {fileAttachments.length > 0 ? (
            <Box
              mt={1.5}
              p={1.25}
              sx={{
                bgcolor: 'warning.lighter',
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                <AttachFileIcon fontSize="small" sx={{ color: 'warning.dark' }} />
                <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {fileAttachments.length} {fileAttachments.length === 1 ? 'Attachment' : 'Attachments'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {fileAttachments.map((att) => {
                  const sizeLabel = att.size
                    ? att.size > 1024 * 1024
                      ? `${(att.size / (1024 * 1024)).toFixed(1)} MB`
                      : `${Math.max(1, Math.round(att.size / 1024))} KB`
                    : null;
                  return (
                    <Chip
                      key={att.id}
                      size="medium"
                      icon={<AttachFileIcon />}
                      deleteIcon={<DownloadIcon />}
                      onDelete={att.file_url ? () => window.open(att.file_url, '_blank') : undefined}
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="baseline">
                          <Typography variant="body2" fontWeight={600} component="span">
                            {att.filename || 'Attachment'}
                          </Typography>
                          {sizeLabel ? (
                            <Typography variant="caption" color="text.secondary" component="span">
                              {sizeLabel}
                            </Typography>
                          ) : null}
                        </Stack>
                      }
                      onClick={att.file_url ? () => window.open(att.file_url, '_blank') : undefined}
                      sx={{
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'warning.main',
                        cursor: att.file_url ? 'pointer' : 'default',
                        '&:hover': att.file_url ? { bgcolor: 'warning.lighter' } : undefined,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : null}
          {bodyIsHtml ? (
            <Box
              mt={1.5}
              sx={{
                maxHeight: 420,
                overflow: 'auto',
                '& img': { maxWidth: '100%' },
                fontSize: 14,
                lineHeight: 1.65,
                '& p': { mt: 0, mb: 1.25 },
                '& p:last-child': { mb: 0 },
              }}
              dangerouslySetInnerHTML={{ __html: resolvedHtml }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                mt: 1.5,
                maxHeight: 420,
                overflow: 'auto',
                lineHeight: 1.65,
              }}
            >
              {msg.body_text || 'No body text available.'}
            </Typography>
          )}
          {imageAttachments.length > 0 ? (
            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
              {imageAttachments.map((att) => (
                <Box
                  key={att.id}
                  component="img"
                  src={att.file_url}
                  alt={att.filename || 'Attached image'}
                  title={att.filename || 'Attached image'}
                  onClick={() => window.open(att.file_url, '_blank')}
                  sx={{
                    height: 110,
                    maxWidth: 200,
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2 },
                  }}
                />
              ))}
            </Stack>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}

export default ThreadMessage;
